import { EyeOff, FileText, Mic, Plus, Poll, Send, Smile, Timer, Upload, X } from '@/components/ui/icons';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { LIMITS, uploadLimitBytes, type EmojiDTO, type MessageTargetRef, type PublicUser, type RoleDTO } from '@clanfire/shared';
import { Avatar } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/Button';
import { Menu } from '@/components/ui/Popover';
import { emitTyping, resetTypingThrottle } from '@/realtime/socket';
import { searchEmoji, type EmojiEntry } from '@/lib/emoji';
import { encodeComposerText } from '@/lib/compose';
import { sendMessage } from '@/lib/messaging';
import { useChannelPermissions } from '@/lib/permissions';
import { formatSlowMode, useSlowModeWait } from '@/lib/slowMode';
import { clientConfig } from '@/lib/session';
import { cn, formatBytes } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { hasBlazeNow } from '@/stores/blaze';
import { targetKey, useMessages } from '@/stores/messages';
import { memberName, useServerEmojis, useServerMembers, useServerRoles, useServers } from '@/stores/servers';
import { useConversations } from '@/stores/social';
import { toast, useConfirm, useUi } from '@/stores/ui';
import { useUsers } from '@/stores/users';
import { getDraft, setDraft } from '@/lib/drafts';
import { emojiLabel } from './CustomEmoji';
import { EmojiPicker } from './EmojiPicker';
import { GifPicker } from './GifPicker';
import { voiceRecordingSupported, VoiceRecorder } from './VoiceRecorder';
import { gifsEnabled, type GifResult } from '@/lib/gifs';

type Suggestion =
  | { kind: 'user'; user: PublicUser }
  | { kind: 'role'; role: RoleDTO }
  | { kind: 'emoji'; entry: EmojiEntry }
  | { kind: 'customEmoji'; emoji: EmojiDTO }
  | { kind: 'everyone' };

const suggestionKey = (s: Suggestion) =>
  s.kind === 'everyone' ? '@everyone' : s.kind === 'user' ? s.user.id : s.kind === 'role' ? s.role.id : s.kind === 'customEmoji' ? s.emoji.id : s.entry.emoji;

interface ComposerProps {
  target: MessageTargetRef;
  serverId?: string;
  placeholder: string;
  canSend: boolean;
  canAttach: boolean;
  disabledReason?: string;
  incomingFiles: File[];
  onIncomingConsumed: () => void;
}

export function Composer({ target, serverId, placeholder, canSend, canAttach, disabledReason, incomingFiles, onIncomingConsumed }: ComposerProps) {
  const key = targetKey(target);
  const [value, setValueState] = useState(() => getDraft(key));
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [caret, setCaret] = useState(0);
  const [acIndex, setAcIndex] = useState(0);
  const [acDismissed, setAcDismissed] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const gifButton = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButton = useRef<HTMLButtonElement>(null);
  const plusButton = useRef<HTMLButtonElement>(null);
  const [plusOpen, setPlusOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const composing = useRef(false);

  const replyTo = useUi((s) => s.replyTo[key] ?? null);
  // "@ ON/OFF": whether replying pings the author. The last choice is remembered.
  const [replyPing, setReplyPingState] = useState(() => {
    try {
      return localStorage.getItem('clanfire:reply-ping') !== 'false';
    } catch {
      return true;
    }
  });
  const setReplyPing = (value: boolean) => {
    setReplyPingState(value);
    try {
      localStorage.setItem('clanfire:reply-ping', String(value));
    } catch {
      /* ignore */
    }
  };
  const setReplyTo = useUi((s) => s.setReplyTo);
  const setEditing = useUi((s) => s.setEditing);
  const me = useAuth((s) => s.user);
  const members = useServerMembers(serverId);
  const users = useUsers((s) => s.users);
  const recipients = useConversations((s) => (target.conversationId ? s.conversations[target.conversationId]?.recipients : undefined));
  const openModal = useUi((s) => s.openModal);
  const roles = useServerRoles(serverId);
  const serverEmojis = useServerEmojis(serverId);
  const allEmojis = useServers((s) => s.emojis);
  const channelPerms = useChannelPermissions(target.channelId);
  // Slow mode doesn't apply to people who manage messages or channels.
  const slowModeSeconds = useServers((s) => (target.channelId ? (s.channels[target.channelId]?.slowModeSeconds ?? 0) : 0));
  const slowModeApplies = slowModeSeconds > 0 && !channelPerms.can('MANAGE_MESSAGES') && !channelPerms.can('MANAGE_CHANNELS');
  const slowWait = useSlowModeWait(target.channelId);

  const setValue = (v: string) => {
    setValueState(v);
    setDraft(key, v);
  };

  /* Mention candidates ------------------------------------------------ */
  const candidates = useMemo<PublicUser[]>(() => {
    if (serverId) return members.map((m) => users[m.userId] ?? m.user);
    return [...(recipients ?? []).map((u) => users[u.id] ?? u), me].filter((u): u is PublicUser => !!u);
  }, [serverId, members, users, recipients, me]);

  /* Autocomplete ------------------------------------------------------ */
  const before = value.slice(0, caret);
  const mentionMatch = /(^|\s)@([a-zA-Z0-9_.]{0,32})$/.exec(before);
  const emojiMatch = !mentionMatch ? /(^|\s):([a-z0-9_+-]{2,32})$/i.exec(before) : null;

  const suggestions = useMemo<Suggestion[]>(() => {
    if (mentionMatch) {
      const q = mentionMatch[2]!.toLowerCase();
      const users: Suggestion[] = candidates
        .filter((u) => u.username.startsWith(q) || u.displayName.toLowerCase().includes(q) || !!memberName(serverId, u.id)?.toLowerCase().includes(q))
        .slice(0, 8)
        .map((user) => ({ kind: 'user', user }));
      // Roles that would ping: mentionable ones, or any role for people who manage roles.
      const mentionable: Suggestion[] = roles
        .filter((r) => !r.isDefault && (r.mentionable || channelPerms.can('MANAGE_ROLES')) && r.name.toLowerCase().includes(q))
        .slice(0, 4)
        .map((role) => ({ kind: 'role', role }));
      const everyone: Suggestion[] = serverId && q && 'everyone'.startsWith(q) && channelPerms.can('MENTION_EVERYONE') ? [{ kind: 'everyone' }] : [];
      return [...everyone, ...users, ...mentionable].slice(0, 10);
    }
    if (emojiMatch) {
      const q = emojiMatch[2]!.toLowerCase();
      // This server's emoji first, then your other servers'.
      const custom: Suggestion[] = [...serverEmojis, ...Object.values(allEmojis).filter((e) => e.serverId !== serverId)]
        .filter((e) => e.name.toLowerCase().includes(q))
        .slice(0, 4)
        .map((emoji) => ({ kind: 'customEmoji', emoji }));
      return [...custom, ...searchEmoji(emojiMatch[2]!, 8).map((entry): Suggestion => ({ kind: 'emoji', entry }))].slice(0, 8);
    }
    return [];
  }, [mentionMatch?.[2], emojiMatch?.[2], candidates, roles, serverEmojis, allEmojis, channelPerms]);

  const acOpen = suggestions.length > 0 && !acDismissed;
  useEffect(() => {
    setAcIndex(0);
    setAcDismissed(false);
  }, [mentionMatch?.[2], emojiMatch?.[2]]);

  const applySuggestion = (s: Suggestion) => {
    const match = mentionMatch ?? emojiMatch;
    if (!match) return;
    const tokenStart = before.length - match[0].length + match[1]!.length;
    const insert =
      s.kind === 'everyone'
        ? '@everyone '
        : s.kind === 'user'
          ? `@${s.user.username} `
          : s.kind === 'role'
            ? `@${s.role.name} `
            : s.kind === 'customEmoji'
              ? `:${s.emoji.name}: `
              : `${s.entry.emoji} `;
    const next = value.slice(0, tokenStart) + insert + value.slice(caret);
    setValue(next);
    const pos = tokenStart + insert.length;
    requestAnimationFrame(() => {
      textareaRef.current?.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  /* Files ------------------------------------------------------------- */
  const addFiles = (list: File[]) => {
    if (!list.length) return;
    if (!canAttach) return toast.error("You don't have permission to attach files here.");
    // Blaze, and the server's level, raise the limit.
    const stokeCount = serverId ? (useServers.getState().servers[serverId]?.stokeCount ?? 0) : 0;
    const maxBytes = uploadLimitBytes(clientConfig.maxUploadBytes, { blaze: hasBlazeNow(), stokeCount });
    const tooBig = list.find((f) => f.size > maxBytes);
    if (tooBig) return toast.error(`"${tooBig.name}" is larger than ${formatBytes(maxBytes)}.${hasBlazeNow() ? '' : ' Blaze raises the limit.'}`);
    setFiles((current) => {
      const next = [...current, ...list.map((file) => ({ file, spoiler: false, description: '' }))];
      if (next.length > LIMITS.attachmentsPerMessage) toast.error(`You can attach up to ${LIMITS.attachmentsPerMessage} files.`);
      return next.slice(0, LIMITS.attachmentsPerMessage);
    });
    textareaRef.current?.focus();
  };

  useEffect(() => {
    if (incomingFiles.length) {
      addFiles(incomingFiles);
      onIncomingConsumed();
    }
  }, [incomingFiles]);

  /* Sizing & focus ---------------------------------------------------- */
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.4)}px`;
  }, [value]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  // Typing anywhere in the chat focuses the composer (desktop keyboards).
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || (active as HTMLElement).isContentEditable)) return;
      if (document.querySelector('dialog[open]')) return;
      textareaRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Send -------------------------------------------------------------- */
  const send = () => {
    const trimmed = value.trim();
    if (!trimmed && files.length === 0) return;
    if (trimmed.length > LIMITS.messageMax) return toast.error(`Messages can be at most ${LIMITS.messageMax} characters.`);
    if (slowModeApplies && slowWait > 0) return toast.error(`Slow mode is on: wait ${formatSlowMode(slowWait)} before sending again.`);
    const byUsername = new Map(candidates.map((u) => [u.username.toLowerCase(), u.id]));
    const content = encodeComposerText(trimmed, { serverId, usernames: byUsername });
    const attachments = files.map(({ spoiler, description }) => ({ spoiler, description: description || undefined }));
    void sendMessage(target, { content, files: files.map((f) => f.file), attachments, replyTo, replyPing, serverId, slowModeSeconds: slowModeApplies ? slowModeSeconds : 0 });
    setValue('');
    setFiles([]);
    setReplyTo(key, null);
    resetTypingThrottle(target);
  };

  /** A voice message goes out on its own when you press send on the recording. */
  const [recording, setRecording] = useState(false);
  const startRecording = () => {
    if (slowModeApplies && slowWait > 0) return toast.error(`Slow mode is on: wait ${formatSlowMode(slowWait)} before sending again.`);
    setRecording(true);
  };
  const sendVoice = (file: File, voice: { durationMs: number; waveform: string }) => {
    setRecording(false);
    void sendMessage(target, { content: '', files: [file], replyTo, replyPing, serverId, slowModeSeconds: slowModeApplies ? slowModeSeconds : 0, voice });
    setReplyTo(key, null);
  };
  const canRecord = canAttach && voiceRecordingSupported();

  /** A GIF goes out on its own, straight away (like most chat apps). */
  const sendGif = (gif: GifResult) => {
    setGifOpen(false);
    if (slowModeApplies && slowWait > 0) return toast.error(`Slow mode is on: wait ${formatSlowMode(slowWait)} before sending again.`);
    void sendMessage(target, { content: gif.url, files: [], replyTo, replyPing, serverId, slowModeSeconds: slowModeApplies ? slowModeSeconds : 0 });
    setReplyTo(key, null);
    textareaRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (composing.current || e.nativeEvent.isComposing) return;
    if (acOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        return setAcIndex((i) => (i + 1) % suggestions.length);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        return setAcIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        return applySuggestion(suggestions[acIndex]!);
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        return setAcDismissed(true);
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    } else if (e.key === 'Escape' && replyTo) {
      e.preventDefault(); // consumed: don't also mark the channel read
      setReplyTo(key, null);
    } else if (e.key === 'ArrowUp' && value === '' && me) {
      const mine = [...(useMessages.getState().byTarget[key]?.messages ?? [])].reverse().find((m) => m.author?.id === me.id && !m.pending && !m.failed);
      if (mine) {
        e.preventDefault();
        setEditing(key, mine.id);
      }
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = [...e.clipboardData.files];
    if (pasted.length) {
      e.preventDefault();
      addFiles(pasted);
    }
  };

  if (!canSend) {
    return (
      <div className="rounded-full bg-sunken px-5 py-3.5 text-sm text-fg-muted" role="note">
        {disabledReason ?? "You don't have permission to send messages here."}
      </div>
    );
  }

  const replyAuthor = replyTo?.author ? (users[replyTo.author.id] ?? replyTo.author) : null;

  return (
    <div className="relative">
      {acOpen && (
        <div role="listbox" aria-label="Suggestions" className="absolute inset-x-0 bottom-full z-20 mb-2 overflow-hidden rounded-3xl border border-line bg-elevated p-2 shadow-pop">
          <p className="px-2 pt-1 pb-1.5 text-[11px] font-bold tracking-wide text-fg-muted uppercase">{mentionMatch ? 'Members and roles' : 'Emoji matching'}</p>
          {suggestions.map((s, i) => (
            <button
              key={suggestionKey(s)}
              type="button"
              role="option"
              aria-selected={i === acIndex}
              onMouseEnter={() => setAcIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                applySuggestion(s);
              }}
              className={cn('flex w-full items-center gap-2.5 rounded-full px-2.5 py-1.5 text-left text-sm', i === acIndex ? 'bg-active text-fg' : 'text-fg-soft')}
            >
              {s.kind === 'everyone' ? (
                <>
                  <span className="flex h-6 w-6 items-center justify-center font-bold text-link">@</span>
                  <span className="font-semibold">everyone</span>
                  <span className="text-fg-muted">notifies everyone who can see this channel</span>
                </>
              ) : s.kind === 'user' ? (
                <>
                  <Avatar user={s.user} size={24} />
                  <span className="font-semibold">{memberName(serverId, s.user.id) ?? s.user.displayName}</span>
                  <span className="text-fg-muted">@{s.user.username}</span>
                </>
              ) : s.kind === 'role' ? (
                <>
                  <span className="flex h-6 w-6 items-center justify-center font-bold" style={{ color: s.role.color ?? undefined }}>
                    @
                  </span>
                  <span className="font-semibold" style={{ color: s.role.color ?? undefined }}>
                    {s.role.name}
                  </span>
                  <span className="text-fg-muted">role</span>
                </>
              ) : s.kind === 'customEmoji' ? (
                <>
                  <img src={s.emoji.url} alt="" className="h-6 w-6 object-contain" />
                  <span>:{s.emoji.name}:</span>
                </>
              ) : (
                <>
                  <span className="text-xl">{s.entry.emoji}</span>
                  <span>:{s.entry.name}:</span>
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {replyTo && (
        <div className="mx-5 flex items-center gap-2 rounded-t-2xl bg-well px-4 py-1.5 text-[13px] text-fg-muted">
          <span className="min-w-0 flex-1 truncate">
            Replying to <strong className="text-fg-soft">{memberName(serverId, replyAuthor?.id) ?? replyAuthor?.displayName ?? 'Deleted User'}</strong>
          </span>
          {replyTo.author && replyTo.author.id !== me?.id && (
            <button
              type="button"
              onClick={() => setReplyPing(!replyPing)}
              aria-pressed={replyPing}
              title={replyPing ? 'They will be notified — click to reply without pinging' : 'They won’t be notified — click to ping them'}
              className={cn('rounded-md px-1.5 py-0.5 text-xs font-bold transition-colors', replyPing ? 'text-link hover:bg-link/15' : 'text-fg-muted hover:bg-hover')}
            >
              @ {replyPing ? 'ON' : 'OFF'}
            </button>
          )}
          <button type="button" onClick={() => setReplyTo(key, null)} aria-label="Cancel reply" className="rounded-full p-0.5 hover:bg-hover hover:text-fg">
            <X size={16} />
          </button>
        </div>
      )}

      <div data-cf="composer" className="rounded-[26px] bg-sunken ring-1 ring-transparent transition-shadow focus-within:ring-line-strong">
        {files.length > 0 && (
          <AttachmentTray
            files={files}
            onRemove={(i) => setFiles((f) => f.filter((_, j) => j !== i))}
            onChange={(i, patch) => setFiles((f) => f.map((item, j) => (j === i ? { ...item, ...patch } : item)))}
          />
        )}
        {recording ? (
          <VoiceRecorder onSend={sendVoice} onCancel={() => setRecording(false)} />
        ) : (
        <div className="flex items-end gap-1.5 p-2">
          <IconButton ref={plusButton} label="Upload a file or create a poll" active={plusOpen} onClick={() => setPlusOpen((v) => !v)} className="border border-line text-fg-soft!">
            <Plus size={18} />
          </IconButton>
          <Menu
            open={plusOpen}
            onClose={() => setPlusOpen(false)}
            anchor={plusButton}
            placement="top-start"
            label="Add to your message"
            items={[
              { label: 'Upload a file', icon: <Upload size={16} />, disabled: !canAttach, onSelect: () => fileInput.current?.click() },
              { label: 'Create a poll', icon: <Poll size={16} />, onSelect: () => openModal({ type: 'createPoll', target }) },
            ]}
          />
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            accept={clientConfig.allowedUploadTypes.join(',')}
            onChange={(e) => {
              addFiles([...(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            placeholder={placeholder}
            aria-label={placeholder}
            maxLength={LIMITS.messageMax + 500}
            onChange={(e) => {
              setValue(e.target.value);
              setCaret(e.target.selectionStart);
              if (e.target.value.trim()) emitTyping(target);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onCompositionStart={() => (composing.current = true)}
            onCompositionEnd={() => (composing.current = false)}
            className="max-h-[40vh] min-h-9 flex-1 resize-none bg-transparent px-1.5 py-[7px] text-[15px] leading-[22px] text-fg placeholder:text-fg-muted focus:outline-none"
          />
          {gifsEnabled() && (
            <button
              ref={gifButton}
              type="button"
              aria-label="Send a GIF"
              aria-expanded={gifOpen}
              onClick={() => setGifOpen((v) => !v)}
              className={cn(
                'flex h-9 shrink-0 items-center rounded-full px-2 text-[12px] font-extrabold tracking-wide transition-colors',
                gifOpen ? 'bg-active text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
              )}
            >
              <span className="rounded-[5px] border-2 border-current px-1 leading-4">GIF</span>
            </button>
          )}
          <IconButton ref={emojiButton} label="Insert emoji" active={emojiOpen} onClick={() => setEmojiOpen((v) => !v)}>
            <Smile size={21} />
          </IconButton>
          {canRecord && !value.trim() && files.length === 0 ? (
            <IconButton label="Record a voice message" onClick={startRecording} className="bg-accent text-accent-fg! hover:bg-accent-hover!">
              <Mic size={18} />
            </IconButton>
          ) : (
          <button
            type="button"
            onClick={send}
            disabled={(!value.trim() && files.length === 0) || (slowModeApplies && slowWait > 0)}
            aria-label={slowModeApplies && slowWait > 0 ? `Slow mode: you can send again in ${formatSlowMode(slowWait)}` : 'Send message'}
            className="flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-accent font-display text-sm text-accent-fg transition-[background-color,opacity] hover:bg-accent-hover disabled:opacity-45 sm:w-auto sm:px-4"
          >
            <span className="hidden sm:inline">Send</span>
            <Send size={16} />
          </button>
          )}
        </div>
        )}
      </div>
      {slowModeSeconds > 0 && (
        <p className="mt-1 flex items-center justify-end gap-1 text-xs text-fg-muted" aria-live="polite">
          <Timer size={13} />
          {!slowModeApplies
            ? `Slow mode is on (${formatSlowMode(slowModeSeconds)}), but not for you`
            : slowWait > 0
              ? `Slow mode: you can send again in ${formatSlowMode(slowWait)}`
              : `Slow mode is on: one message every ${formatSlowMode(slowModeSeconds)}`}
        </p>
      )}
      {value.length > LIMITS.messageMax - 400 && (
        <p className={cn('mt-1 text-right text-xs', value.length > LIMITS.messageMax ? 'font-bold text-danger' : 'text-fg-muted')}>
          {LIMITS.messageMax - value.length}
        </p>
      )}
      {gifsEnabled() && <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} anchor={gifButton} onSelect={sendGif} />}
      <EmojiPicker
        open={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        anchor={emojiButton}
        serverId={serverId}
        onSelect={(picked) => {
          // Custom emoji go in as :name: (turned into the emoji when sent).
          const emoji = emojiLabel(picked);
          const el = textareaRef.current;
          const start = el?.selectionStart ?? value.length;
          const end = el?.selectionEnd ?? value.length;
          const next = value.slice(0, start) + emoji + value.slice(end);
          setValue(next);
          requestAnimationFrame(() => {
            el?.focus();
            el?.setSelectionRange(start + emoji.length, start + emoji.length);
          });
        }}
      />
    </div>
  );
}

/** A file waiting to be sent, with whether it's a spoiler and its description (alt text). */
interface PendingFile {
  file: File;
  spoiler: boolean;
  description: string;
}

function AttachmentTray({ files, onRemove, onChange }: { files: PendingFile[]; onRemove: (index: number) => void; onChange: (index: number, patch: Partial<PendingFile>) => void }) {
  const previews = useMemo(() => files.map(({ file }) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : null)), [files]);
  useEffect(() => () => previews.forEach((p) => p && URL.revokeObjectURL(p)), [previews]);
  const describe = async (i: number) => {
    const result = await useConfirm.getState().open({
      title: 'Describe this',
      body: 'People using screen readers hear this instead of seeing it.',
      confirmLabel: 'Save',
      input: { label: 'Description', placeholder: 'A campfire on a beach at sunset', value: files[i]!.description },
    });
    if (result.confirmed) onChange(i, { description: result.value.trim().slice(0, LIMITS.attachmentDescriptionMax) });
  };
  return (
    <div className="flex gap-3 overflow-x-auto border-b border-line p-3">
      {files.map(({ file, spoiler, description }, i) => (
        <div key={`${file.name}-${i}`} className="relative flex w-40 shrink-0 flex-col gap-1.5 rounded-2xl bg-surface p-2">
          <button
            type="button"
            onClick={() => onRemove(i)}
            aria-label={`Remove ${file.name}`}
            className="absolute -top-2 -right-2 rounded-full border border-line bg-elevated p-1 text-danger shadow hover:bg-danger hover:text-white"
          >
            <X size={14} />
          </button>
          {previews[i] ? (
            <img src={previews[i]!} alt="" className={cn('h-24 w-full rounded-lg object-cover', spoiler && 'blur-md')} />
          ) : (
            <div className="flex h-24 items-center justify-center rounded-lg bg-hover text-fg-muted">
              <FileText size={32} />
            </div>
          )}
          <span className="truncate text-xs text-fg-soft" title={file.name}>
            {file.name}
          </span>
          <span className="text-[11px] text-fg-muted">{formatBytes(file.size)}</span>
          {/^(image|video)\//.test(file.type) && (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onChange(i, { spoiler: !spoiler })}
                aria-pressed={spoiler}
                className={cn('inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-full text-[11px] font-bold', spoiler ? 'bg-accent text-accent-fg' : 'bg-hover text-fg-soft hover:text-fg')}
              >
                <EyeOff size={13} aria-hidden /> Spoiler
              </button>
              <button
                type="button"
                onClick={() => void describe(i)}
                aria-label={description ? `Edit description: ${description}` : 'Add a description'}
                title={description || 'Add a description for screen readers'}
                className={cn('inline-flex h-7 flex-1 items-center justify-center rounded-full text-[11px] font-bold', description ? 'bg-accent text-accent-fg' : 'bg-hover text-fg-soft hover:text-fg')}
              >
                ALT
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
